"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import type {
  PurchaseSelection,
  StoreProduct,
  StoreVariant,
} from "@/components/store/types";

import { trackEvent } from "@/lib/analytics";

export type CartItem = Readonly<{
  productId: string;
  productName: string;
  image?: {
    id: string;
    url: string;
    transformedUrl?: string;
  };
  variant: StoreVariant;
  quantity: number;
}>;

type CartContextType = Readonly<{
  cartItems: readonly CartItem[];
  isCartOpen: boolean;
  isCheckingOut: boolean;
  checkoutError: string;
  cartCount: number;
  setCartOpen: (open: boolean) => void;
  addToCart: (product: StoreProduct, selection: PurchaseSelection) => void;
  updateQuantity: (itemKey: string, quantity: number) => void;
  checkout: () => Promise<void>;
  clearCart: () => void;
}>;

const CartContext = createContext<CartContextType | undefined>(undefined);

const getItemKey = (item: Pick<CartItem, "productId" | "variant">): string => {
  return `${item.productId}:${item.variant.id}`;
};

export const CartProvider = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>): React.ReactElement => {
  const [cartItems, setCartItems] = useState<readonly CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  // Load cart from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("simpleble_cart");
      if (stored) {
        const parsed = JSON.parse(stored) as readonly CartItem[];
        setTimeout(() => {
          setCartItems(parsed);
        }, 0);
      }
    } catch (e) {
      console.error("Failed to load cart from localStorage", e);
    } finally {
      setTimeout(() => {
        setIsInitialized(true);
      }, 0);
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    if (!isInitialized) return;
    try {
      localStorage.setItem("simpleble_cart", JSON.stringify(cartItems));
    } catch (e) {
      console.error("Failed to save cart to localStorage", e);
    }
  }, [cartItems, isInitialized]);

  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const addToCart = (
    product: StoreProduct,
    selection: PurchaseSelection,
  ): void => {
    const colorImages = selection.variant.colorName
      ? product.imagesByColor[selection.variant.colorName]
      : undefined;

    const nextItem: CartItem = {
      productId: product.id,
      productName: product.name,
      image: colorImages?.[0] ?? product.images[0],
      variant: selection.variant,
      quantity: selection.quantity,
    };

    setCartItems((current) => {
      const itemKey = getItemKey(nextItem);
      const existingItem = current.find((item) => getItemKey(item) === itemKey);

      if (!existingItem) {
        return [...current, nextItem];
      }

      return current.map((item) => {
        if (getItemKey(item) !== itemKey) {
          return item;
        }

        return {
          ...item,
          quantity: Math.min(10, item.quantity + selection.quantity),
        };
      });
    });
    setIsCartOpen(true);

    trackEvent("add_to_cart", {
      product_name: product.name,
      variant_color: selection.variant.colorName,
      variant_size: selection.variant.sizeName,
      quantity: selection.quantity,
      value: selection.variant.priceValue * selection.quantity,
      currency: selection.variant.currency,
    });
  };

  const updateQuantity = (itemKey: string, quantity: number): void => {
    if (quantity < 1) {
      setCartItems((current) =>
        current.filter((item) => getItemKey(item) !== itemKey),
      );
      return;
    }

    setCartItems((current) =>
      current.map((item) => {
        if (getItemKey(item) !== itemKey) {
          return item;
        }

        return { ...item, quantity: Math.min(10, quantity) };
      }),
    );
  };

  const clearCart = (): void => {
    setCartItems([]);
  };

  const getCheckoutUrl = (value: unknown): string | null => {
    if (
      typeof value !== "object" ||
      value === null ||
      !("checkoutUrl" in value)
    ) {
      return null;
    }

    const checkoutUrl = value.checkoutUrl;

    return typeof checkoutUrl === "string" ? checkoutUrl : null;
  };

  const checkout = async (): Promise<void> => {
    if (cartItems.length === 0 || isCheckingOut) {
      return;
    }

    setCheckoutError("");
    setIsCheckingOut(true);

    trackEvent("begin_checkout", {
      item_count: cartCount,
      value: cartItems.reduce(
        (total, item) => total + item.variant.priceValue * item.quantity,
        0,
      ),
      currency: cartItems[0]?.variant.currency,
    });

    try {
      const response = await fetch("/api/store/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: cartItems.map((item) => ({
            variantId: item.variant.id,
            quantity: item.quantity,
          })),
          metadata: { source: "simpleble-storefront" },
        }),
      });

      const data = (await response.json()) as unknown;
      const checkoutUrl = getCheckoutUrl(data);

      if (!response.ok || !checkoutUrl) {
        setCheckoutError("Checkout is unavailable right now.");
        trackEvent("checkout_error", { status: response.status });
        return;
      }

      // Clear the local cart right before redirecting
      clearCart();
      window.location.href = checkoutUrl;
    } catch {
      setCheckoutError("Checkout is unavailable right now.");
      trackEvent("checkout_error", { status: "network" });
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        isCheckingOut,
        checkoutError,
        cartCount,
        setCartOpen: setIsCartOpen,
        addToCart,
        updateQuantity,
        checkout,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
